import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { App } from './App';
import './styles/globals.css';

function RouterErrorPanel({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div
      style={{
        padding: '1.5rem',
        fontFamily: 'system-ui, sans-serif',
        color: '#e8e4d8',
        background: '#14130f',
        minHeight: '100vh',
      }}
    >
      <h1 style={{ marginTop: 0, color: '#d97a7a' }}>JND Bible — route crash</h1>
      <p>Caught by the router. Please screenshot this and send it to the maintainer.</p>
      <h2 style={{ fontSize: '1rem', marginTop: '1.5rem' }}>Error message</h2>
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          background: '#1c1b17',
          padding: '0.75rem',
          borderRadius: 6,
          fontSize: 13,
          border: '1px solid #2f2e28',
        }}
      >
        {String(error?.message ?? error)}
      </pre>
      <h2 style={{ fontSize: '1rem', marginTop: '1.5rem' }}>JS stack</h2>
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          background: '#1c1b17',
          padding: '0.75rem',
          borderRadius: 6,
          fontSize: 11,
          border: '1px solid #2f2e28',
          maxHeight: '40vh',
          overflow: 'auto',
        }}
      >
        {error?.stack ?? '(no stack)'}
      </pre>
      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={reset}
          style={{
            padding: '0.6rem 1rem',
            background: 'transparent',
            color: '#e8e4d8',
            border: '1px solid #2f2e28',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
        <button
          type="button"
          onClick={async () => {
            if ('serviceWorker' in navigator) {
              const regs = await navigator.serviceWorker.getRegistrations();
              await Promise.all(regs.map((r) => r.unregister()));
            }
            if ('caches' in window) {
              const keys = await caches.keys();
              await Promise.all(keys.map((k) => caches.delete(k)));
            }
            try {
              localStorage.clear();
            } catch {
              /* ignore */
            }
            location.reload();
          }}
          style={{
            padding: '0.6rem 1rem',
            background: '#c9a96a',
            color: '#14130f',
            border: 0,
            borderRadius: 6,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Clear cache &amp; reload
        </button>
      </div>
    </div>
  );
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 30_000,
  scrollRestoration: true,
  defaultErrorComponent: RouterErrorPanel,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

interface ErrorBoundaryState {
  error: Error | null;
  info: React.ErrorInfo | null;
}

class TopLevelErrorBoundary extends React.Component<
  React.PropsWithChildren,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ error, info });
    console.error('[JND] caught:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: '1.5rem',
            fontFamily: 'system-ui, sans-serif',
            color: '#e8e4d8',
            background: '#14130f',
            minHeight: '100vh',
          }}
        >
          <h1 style={{ marginTop: 0, color: '#d97a7a' }}>JND Bible — crash report</h1>
          <p>The app hit an unrecoverable error. Please share this with the maintainer.</p>
          <h2 style={{ fontSize: '1rem', marginTop: '1.5rem' }}>Message</h2>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: '#1c1b17',
              padding: '0.75rem',
              borderRadius: 6,
              fontSize: 13,
              border: '1px solid #2f2e28',
            }}
          >
            {this.state.error.message}
          </pre>
          <h2 style={{ fontSize: '1rem', marginTop: '1.5rem' }}>Component stack</h2>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: '#1c1b17',
              padding: '0.75rem',
              borderRadius: 6,
              fontSize: 12,
              border: '1px solid #2f2e28',
            }}
          >
            {this.state.info?.componentStack ?? '(no component stack)'}
          </pre>
          <h2 style={{ fontSize: '1rem', marginTop: '1.5rem' }}>JS stack</h2>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: '#1c1b17',
              padding: '0.75rem',
              borderRadius: 6,
              fontSize: 12,
              border: '1px solid #2f2e28',
            }}
          >
            {this.state.error.stack ?? '(no stack)'}
          </pre>
          <button
            type="button"
            onClick={() => {
              if ('serviceWorker' in navigator) {
                void navigator.serviceWorker.getRegistrations().then((regs) => {
                  for (const r of regs) void r.unregister();
                });
              }
              if ('caches' in window) {
                void caches.keys().then((keys) => keys.forEach((k) => void caches.delete(k)));
              }
              try {
                localStorage.clear();
              } catch {
                /* ignore */
              }
              location.reload();
            }}
            style={{
              marginTop: '1.5rem',
              padding: '0.6rem 1rem',
              background: '#c9a96a',
              color: '#14130f',
              border: 0,
              borderRadius: 6,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Clear cache &amp; reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('No #root');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <TopLevelErrorBoundary>
      <App>
        <RouterProvider router={router} />
      </App>
    </TopLevelErrorBoundary>
  </React.StrictMode>,
);

// Service worker registration is auto-injected by vite-plugin-pwa with registerType: 'autoUpdate'.
